import { Controller } from "@hotwired/stimulus"

// The thread header's request-type picker: several types may be on at once, so toggling one
// submits the PATCH form and the menu STAYS OPEN to let the next one be picked — the same shape
// filter_menu_controller uses for the toolbar's stackable filters, rather than dropdown_controller's
// pick-one-and-close.
//
// A type is carried by its own hidden input, enabled or disabled; a disabled input is not
// submitted, so toggling is a flag flip on server-rendered markup and this controller never builds
// any DOM. The chips region re-renders from the response (see update.turbo_stream.erb), while the
// menu and the trigger live outside it — so the checked state and the "Clear all" row are moved
// here, exactly as the two sibling controllers move theirs.
export default class extends Controller {
  static targets = ["menu", "field", "clear"]

  connect() {
    this.closeOnOutside = this.closeOnOutside.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
  }

  disconnect() {
    this.removeListeners()
  }

  toggleMenu() {
    this.menuTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.menuTarget.hidden = false
    this.button.setAttribute("aria-expanded", "true")
    // Several toggles per visit is the whole point of this menu, so a keyboard user is put inside
    // it rather than left to tab in from the trigger each time.
    this.options[0]?.focus()
    document.addEventListener("click", this.closeOnOutside)
    document.addEventListener("keydown", this.closeOnEscape)
  }

  close() {
    this.menuTarget.hidden = true
    this.button.setAttribute("aria-expanded", "false")
    this.removeListeners()
  }

  // Driven by both a menu option and a chip's remove button — same action, same value param, so
  // the two can never disagree about what a click means.
  toggle(event) {
    const field = this.fieldFor(event.params.value)
    if (!field) return

    this.setSelected(field, field.disabled)
    this.submit()
  }

  clear() {
    this.fieldTargets.forEach((field) => this.setSelected(field, false))
    this.submit()
  }

  setSelected(field, selected) {
    field.disabled = !selected
    this.optionFor(field.dataset.value)?.setAttribute("aria-checked", String(selected))
  }

  submit() {
    this.refreshClear()
    this.element.closest("form").requestSubmit()
  }

  // "Clear all" sits outside the region the response swaps, so it is shown and hidden here.
  refreshClear() {
    if (!this.hasClearTarget) return

    this.clearTarget.hidden = !this.fieldTargets.some((field) => !field.disabled)
  }

  // Compared as strings on purpose. Stimulus type-casts action params, so a request type's numeric
  // id arrives here as the Number 3 while dataset.value is always the String "3" — a strict
  // comparison between them silently matches nothing, and the toggle does nothing at all.
  fieldFor(value) {
    return this.fieldTargets.find((field) => field.dataset.value === String(value))
  }

  optionFor(value) {
    return this.menuTarget.querySelector(`[data-multi-dropdown-value-param="${String(value)}"]`)
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
      this.button.focus()
    }
  }

  removeListeners() {
    document.removeEventListener("click", this.closeOnOutside)
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  get options() {
    return Array.from(this.menuTarget.querySelectorAll("[role='menuitemcheckbox']"))
  }

  get button() {
    return this.element.querySelector("[aria-haspopup]")
  }
}
